import React, { ReactNode } from "react";
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  Params,
} from "react-router-dom";
import { Provider, inject, observer } from "mobx-react";
import ProductProtectedRoute from "./ProductProtectedRoute";
import { Subject, takeUntil } from "rxjs";
import { ProductPricingPage, ProductVariantPage } from "./components";
import ProductStore from "./ProductStore";
import {
  Header,
  ItemDetailPage,
  ItemHeader,
  Layout,
  MediaBoxes,
  SideBar,
} from "../../shared/components";
import {
  AuthService,
  Loader,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  CategoryService,
  CountryService,
  ProductService,
  RelatedItemsService,
} from "../../shared/services";
import { MediaTypes, UpsertProductActiveStep } from "../../shared/enums";
import { ProductDetailWrapper } from "../../shared/styles";
import { ItemDetailInputModel, MediaModel } from "../../shared/models";
import RelatedItemsSection from "../../shared/components/RelatedItemsSection";
import { CountryStore, RolesAndPermissionStore } from "../../shared/store";
import { toJS } from "mobx";
import FullLibraryView from "../../shared/components/FullLibraryView";
import { getMerchantAccountStore } from "../merchantAccounts/MerchantAccountStore";

interface IProps {
  productStore?: ProductStore;
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  rolesAndPermissionStore: RolesAndPermissionStore;
  countryStore: CountryStore;
}

type IState = {
  showLibraryView: boolean;
  showUpload: boolean;
  //countriesWithState: CountryWithStateModel[]
};

export const getCategoryService = (() => {
  let instance: CategoryService | null = null;
  return () => {
    if (!instance) {
      instance = new CategoryService();
    }
    return instance;
  };
})();

@inject("productStore")
@observer
class ProductRoutes extends React.Component<IProps, IState> {
  private loader: Loader = new Loader(false);
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private disposeSurchargeDefault?: () => void;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showLibraryView: false,
      showUpload: false,
      // countriesWithState: [],
    };
    // this.loadCountries();
  }

  // loadCountries = () => {

  //   CountryService.getCountriesWithState().subscribe((response) => {
  //     const countries = response.result.map(
  //       (c) => new CountryWithStateModel(c)
  //     );
  //     this.setState({ countriesWithState: countries }, () => {
  //       this.setDefaultCountry();
  //     });

  //   });
  // }

  toggleLibraryView = (show: boolean, fromUpload?: boolean) => {
    this.setState(() => ({
      showLibraryView: show,
      showUpload: fromUpload || false,
    }));
  };

  onCancel() {
    const { activeStep } = this.props.productStore || {};
    if (
      activeStep == UpsertProductActiveStep.Pricing ||
      activeStep == UpsertProductActiveStep.Variants
    ) {
      this.onBackOrNext(false);
      return;
    }
    this.props.navigate(AuthService.prefixRoute + "items");
  }

  get productId() {
    const split = ((this.props.params && this.props.params["*"]) || "").split(
      "/",
    );
    const id = split[split.length - 1];
    if (UtilityMethods.isValidObjectId(id)) {
      return id;
    }
    return undefined;
  }

  setDefaultCountry = () => {
    if (!this.props.productStore) return;
    // if (this.countriesWithState.length === 0) {
    //   const subscription =
    //     this.props.productStore.countryStore.onCountryDataLoaded.subscribe(
    //       () => {
    //         subscription.unsubscribe();
    //         this.setDefaultCountry();
    //       }
    //     );
    //   return;
    // }
    const currentCountryId =
      this.props.productStore.detailPage?.selectedCountry?.id;
    const selectedCountry = currentCountryId
      ? this.props.countryStore.countriesWithState.find(
          (c) => c.id === currentCountryId,
        )
      : this.props.countryStore.getUSCountry();

    if (selectedCountry) {
      this.props.productStore?.updateDetailPage(
        new ItemDetailInputModel({
          ...this.props.productStore.detailPage,
          selectedCountry,
        }),
      );
    }
  };

  componentDidMount(): void {
    const { productStore } = this.props;
    productStore?.reset();
    if (this.productId) {
      this.loader.show();
      ProductService.getProduct(AuthService.authData.accountId, this.productId)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (result) => {
            if (result.result) {
              productStore?.constructStoreFromAPI(result.result);
              this.setDefaultCountry();
            } else {
              ToastService.showError("Error in loading product details");
            }
          },
          error: () => {
            ToastService.showError("Error in loading product details");
          },
          complete: () => {
            this.loader.hide();
          },
        });
    } else {
      this.setDefaultCountry();
      this.setDefaultSurcharge();
    }
  }

  /* A new product picks up the surcharge setting of the merchant account it
     will be sold through. Only runs when creating: an existing product keeps
     the setting it was saved with. */
  setDefaultSurcharge = () => {
    this.disposeSurchargeDefault =
      getMerchantAccountStore().whenItemSurchargeDefaultKnown((surcharge) => {
        const { productStore } = this.props;
        if (!productStore) return;
        const updated = ItemDetailInputModel.withMerchantSurchargeDefault(
          productStore.detailPage,
          surcharge,
        );
        if (updated) productStore.updateDetailPage(updated);
      });
  };

  componentWillUnmount(): void {
    this.$destroy.next(true);
    this.disposeSurchargeDefault?.();
  }

  onBackOrNext(isNext: boolean) {
    if (this.loader.showLoader) return;
    let queryParam: string = "";
    if (this.productId) {
      queryParam = "/" + this.productId;
    }
    if (isNext) {
      const success = this.props.productStore?.validate();
      if (!success) return;
    }
    switch (this.props.productStore?.activeStep) {
      case UpsertProductActiveStep.Detail:
        this.props.productStore?.updateActiveStep(
          UpsertProductActiveStep.Pricing,
        );
        this.props.navigate(
          AuthService.prefixRoute + `product/pricing${queryParam}`,
        );
        break;
      case UpsertProductActiveStep.Pricing:
        if (this.props.productStore?.detailPage.hasVariants) {
          this.props.productStore?.updateActiveStep(
            isNext
              ? UpsertProductActiveStep.Variants
              : UpsertProductActiveStep.Detail,
          );
          isNext
            ? this.props.navigate(
                AuthService.prefixRoute + `product/variants${queryParam}`,
              )
            : this.props.navigate(
                AuthService.prefixRoute + `product/detail${queryParam}`,
              );
        } else {
          this.props.productStore?.updateActiveStep(
            isNext
              ? UpsertProductActiveStep.Options
              : UpsertProductActiveStep.Detail,
          );
          isNext
            ? this.props.navigate(
                AuthService.prefixRoute + `product/options${queryParam}`,
              )
            : this.props.navigate(
                AuthService.prefixRoute + `product/detail${queryParam}`,
              );
        }
        break;
      case UpsertProductActiveStep.Variants:
        this.props.productStore?.updateActiveStep(
          isNext
            ? UpsertProductActiveStep.Options
            : UpsertProductActiveStep.Pricing,
        );
        isNext
          ? this.props.navigate(
              AuthService.prefixRoute + `product/options${queryParam}`,
            )
          : this.props.navigate(
              AuthService.prefixRoute + `product/pricing${queryParam}`,
            );
        break;
      case UpsertProductActiveStep.Options:
        if (this.props.productStore?.detailPage.hasVariants) {
          this.props.productStore?.updateActiveStep(
            UpsertProductActiveStep.Variants,
          );
          this.props.navigate(
            AuthService.prefixRoute + `product/variants${queryParam}`,
          );
        } else {
          this.props.productStore?.updateActiveStep(
            UpsertProductActiveStep.Pricing,
          );
          this.props.navigate(
            AuthService.prefixRoute + `product/pricing${queryParam}`,
          );
        }

        break;
    }
  }

  private stepConfig = {
    [UpsertProductActiveStep.Detail]: {
      name: "details",
      url: AuthService.prefixRoute + "product/detail",
    },
    [UpsertProductActiveStep.Pricing]: {
      name: "pricing",
      url: AuthService.prefixRoute + "product/pricing",
    },
    [UpsertProductActiveStep.Variants]: {
      name: "variants",
      url: AuthService.prefixRoute + "product/variants",
    },
    [UpsertProductActiveStep.Options]: {
      name: "options",
      url: AuthService.prefixRoute + "product/options",
    },
  };

  onTabChange = (key: string) => {
    const step = key as UpsertProductActiveStep;
    const queryParam = this.productId || "";

    const validationResult =
      this.props.productStore?.validatePreviousSteps(step);
    if (!validationResult) {
      ToastService.showError("Unable to validate steps.");
      return;
    }

    const { isValid, errorStep } = validationResult;
    if (!isValid && errorStep) {
      ToastService.showError(
        `Please fill all the required fields in product ${this.stepConfig[errorStep].name} before proceeding.`,
      );
      this.props.productStore?.updateActiveStep(errorStep);
      this.props.navigate(`${this.stepConfig[errorStep].url}/${queryParam}`);
      return;
    }

    this.props.productStore?.updateActiveStep(step);
    this.props.navigate(`${this.stepConfig[step].url}/${queryParam}`);
  };

  onSave(isSaveAndExit: boolean = false) {
    if (this.loader.showLoader) return;

    if (isSaveAndExit) {
      const validationResult = this.props.productStore?.validatePreviousSteps(
        UpsertProductActiveStep.Options,
      );

      if (!validationResult) {
        ToastService.showError("Validation failed.");
        return;
      }

      const { isValid, errorStep } = validationResult;
      if (!isValid && errorStep) {
        ToastService.showError(
          `Please fill all the required fields in product ${this.stepConfig[errorStep].name} before saving.`,
        );
        this.props.productStore?.updateActiveStep(errorStep);
        this.props.navigate(
          `${this.stepConfig[errorStep].url}/${this.productId || ""}`,
        );
        return;
      }

      const isOptionsValid = this.props.productStore?.validate(
        UpsertProductActiveStep.Options,
      );
      if (!isOptionsValid) {
        ToastService.showError(
          "Please fill all required fields in the Options step before saving.",
        );
        this.props.productStore?.updateActiveStep(
          UpsertProductActiveStep.Options,
        );
        this.props.navigate(
          `${this.stepConfig[UpsertProductActiveStep.Options].url}/${this.productId || ""}`,
        );
        return;
      }
    } else {
      const success = this.props.productStore?.validate();
      if (!success) return;
    }

    if (this.props.productStore?.isSaveUpdateInProgress) return;
    this.props.productStore?.toggleSaveUpdateInProgress();

    if (!this.props.productStore) return;
    const request = this.props.productStore.createSaveModel();

    if (this.productId) {
      request.id = this.productId;
      ProductService.updateProduct(request).subscribe({
        next: (response) => {
          if (response) {
            ToastService.showSuccess("Product updated Successfully");
            this.props.productStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate(AuthService.prefixRoute + "items");
            }
          }
        },
        error: () => {
          ToastService.showError("Product update failed!");
          this.props.productStore?.toggleSaveUpdateInProgress();
        },
      });
    } else {
      ProductService.saveProduct(request).subscribe({
        next: (response) => {
          if (response?.id) {
            ToastService.showSuccess("Product Created Successfully");
            this.props.productStore?.toggleSaveUpdateInProgress();
            if (isSaveAndExit) {
              this.props.navigate(AuthService.prefixRoute + "items");
            }
          }
        },
        error: () => {
          ToastService.showError("Product save failed!");
          this.props.productStore?.toggleSaveUpdateInProgress();
        },
      });
    }
  }

  get showNextButton(): boolean {
    return (
      this.props.productStore?.activeStep !== UpsertProductActiveStep.Options
    );
  }

  get showBackbutton(): boolean {
    return (
      this.props.productStore?.activeStep !== UpsertProductActiveStep.Detail
    );
  }

  get headerCancelButtonCaption(): string {
    return this.props.productStore?.activeStep ===
      UpsertProductActiveStep.Pricing ||
      this.props.productStore?.activeStep == UpsertProductActiveStep.Variants
      ? "Back"
      : "";
  }

  onSelectLibraryMedia(media: MediaModel) {
    this.props.productStore?.updateDetailPage(
      new ItemDetailInputModel({
        ...this.props.productStore?.detailPage,
        medias: [...this.props.productStore?.detailPage.medias, media],
        showLibrary: false,
      }),
    );
  }

  deleteMedia(medias: MediaModel[]) {
    this.props.productStore?.updateDetailPage(
      new ItemDetailInputModel({
        ...this.props.productStore?.detailPage,
        medias,
      }),
    );
  }

  onFavourite(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.productStore?.detailPage?.medias) || []
    ).map((x) => {
      if (x.mediaId === media.mediaId) {
        return { ...x, isFavourite: true } as MediaModel;
      } else if (x.isFavourite) {
        return { ...x, isFavourite: false } as MediaModel;
      }
      return x;
    });
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    if (index !== -1) {
      const [favouritedMedia] = updatedMedia.splice(index, 1);
      updatedMedia.unshift(favouritedMedia);
    }
    this.props.productStore?.updateDetailPage(
      new ItemDetailInputModel({
        ...this.props.productStore?.detailPage,
        medias: [...updatedMedia],
      }),
    );
  }

  onDelete(media: MediaModel) {
    const updatedMedia = (
      toJS(this.props.productStore?.detailPage?.medias) || []
    ).map((x) => x);
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    updatedMedia.splice(index, 1);
    this.deleteMedia(updatedMedia);
  }

  updateMedia(medias: MediaModel[]) {
    this.props.productStore?.updateDetailPage(
      new ItemDetailInputModel({
        ...this.props.productStore?.detailPage,
        medias: [...this.props.productStore?.detailPage.medias, ...medias],
      }),
    );
  }

  get routes() {
    return (
      <Routes>
        <Route
          path={`/detail/:id?`}
          element={
            <ProductProtectedRoute currentRoute="detail">
              <ProductDetailWrapper>
                <ItemHeader header="Product Details" />
                <ItemDetailPage
                  isProduct={true}
                  itemDetail={
                    this.props.productStore?.detailPage ||
                    new ItemDetailInputModel()
                  }
                  onUpdate={(detail: ItemDetailInputModel) =>
                    this.props.productStore?.updateDetailPage(detail)
                  }
                  countriesWithState={
                    this.props.countryStore.countriesWithState || []
                  }
                  toggleLibrary={(show, fromUpload) =>
                    this.toggleLibraryView(show, fromUpload)
                  }
                  showLibraryView={this.state.showLibraryView}
                />
              </ProductDetailWrapper>
            </ProductProtectedRoute>
          }
        />
        <Route
          path={`/pricing/:id?`}
          element={
            <ProductProtectedRoute currentRoute="pricing">
              <ItemHeader header="Product Pricing" />
              <ProductPricingPage />
            </ProductProtectedRoute>
          }
        />
        <Route
          path={`/variants/:id?`}
          element={
            <ProductProtectedRoute currentRoute="variants">
              <ItemHeader header="Product Variants" />
              <ProductVariantPage />
            </ProductProtectedRoute>
          }
        />
        <Route
          path={`/options/:id?`}
          element={
            <ProductProtectedRoute currentRoute="options">
              <ItemHeader header="Product Options" />
              <RelatedItemsSection
                sourceId={this.productId}
                realtedItemService={
                  this.props.productStore?.realtedItemService ||
                  new RelatedItemsService()
                }
                countriesWithState={
                  this.props.countryStore.countriesWithState || []
                }
              />
            </ProductProtectedRoute>
          }
        />
        <Route
          path="*"
          element={
            <Navigate to={AuthService.prefixRoute + "product/detail/"} />
          }
        />
      </Routes>
    );
  }

  render(): ReactNode {
    if (!this.props.countryStore.countryDataLoaded) {
      return <>{new Loader().loaderComponent}</>;
    }
    if (this.state.showLibraryView) {
      return (
        <FullLibraryView
          multiple={true}
          medias={toJS(this.props.productStore?.detailPage.medias || [])}
          onUpload={(medias: MediaModel[]) => this.updateMedia(medias)}
          onDelete={(media: MediaModel) => this.onDelete(media)}
          onSelectLibraryMedia={(media) =>
            this.onSelectLibraryMedia(MediaModel.mediaItemToMediaModel(media))
          }
          onFavourite={(media: MediaModel) => this.onFavourite(media)}
          allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
          toggleLibrary={(show: boolean, fromUpload?: boolean) =>
            this.toggleLibraryView(show, fromUpload)
          }
          showLibrary={true}
          showUpload={this.state.showUpload}
          updateMedia={(medias: MediaModel[]) => this.updateMedia(medias)}
        />
      );
    }
    return (
      <Provider categoryService={getCategoryService()}>
        <Layout
          sideBarComponent={
            <SideBar
              activeStep={this.props.productStore?.activeStep?.toString() || ""}
              showVariant={
                this.props.productStore?.detailPage.hasVariants || false
              }
              onTabChange={(key: UpsertProductActiveStep) =>
                this.onTabChange(key)
              }
            />
          }
          saveAndExitClick={() => this.onSave(true)}
          showSaveAndExit={this.productId ? true : false}
          disableSaveAndExit={
            this.loader.showLoader ||
            !!this.props.productStore?.isSaveUpdateInProgress
          }
          headerCaption={this.props.productStore?.headerCaption || ""}
          cancelClick={() => this.onCancel()}
          nextButtonCaption={this.showNextButton ? "Next" : ""}
          nextClick={() => this.onBackOrNext(true)}
          showBackButton={this.showBackbutton}
          showNextButton={this.showNextButton}
          backClick={() => this.onBackOrNext(false)}
          showHeader={false}
          showFooter={!this.loader.showLoader}
          showCancelButton={false}
          headerComponent={
            <Header
              headerCaption={"CREATE YOUR ITEM TO SELL"}
              showSaveButton={!this.showNextButton}
              saveAndExitButtonCaption={
                this.showNextButton ? "NEXT" : "SAVE & EXIT"
              }
              showSaveAndExitButton={true}
              disableSaveButton={
                this.props.productStore?.isSaveUpdateInProgress ||
                !this.productId
              }
              disableSaveAndExitButton={
                this.loader.showLoader ||
                !!this.props.productStore?.isSaveUpdateInProgress
              }
              saveClick={() => this.onSave()}
              cancelClick={() => this.onCancel()}
              cancelButtonCaption={this.headerCancelButtonCaption}
              saveAndExitClick={() => {
                if (this.loader.showLoader) return;
                this.showNextButton
                  ? this.onBackOrNext(this.showNextButton)
                  : this.onSave(true);
              }}
            />
          }
        >
          {this.loader.showLoader ? this.loader.loaderComponent : this.routes}
        </Layout>
      </Provider>
    );
  }
}

export default withRouter(ProductRoutes);
